import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PageSkeleton } from "@/components/PageSkeleton";
import { PeriodFilter } from "@/components/PeriodFilter";
import { CameraCapture } from "@/components/CameraCapture";
import { usePersistentPeriod } from "@/lib/usePersistentPeriod";
import { formatDate, formatTime } from "@/lib/format";
import { toast } from "sonner";
import { LogIn, LogOut, Clock, CalendarCheck, FileText, Plus, Camera } from "lucide-react";

const fmtMin = (m) => {
  m = Number(m || 0);
  if (m <= 0) return "0 mnt";
  const h = Math.floor(m / 60), mm = m % 60;
  return h > 0 ? `${h} jam ${mm} mnt` : `${mm} mnt`;
};

const CI_BADGE = { "Tepat Waktu": "bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300", "Terlambat": "bg-rose-100 text-rose-800 dark:bg-rose-950 dark:text-rose-300" };
const CO_BADGE = { "Normal": "bg-sky-100 text-sky-800 dark:bg-sky-950 dark:text-sky-300", "Lembur": "bg-indigo-100 text-indigo-800 dark:bg-indigo-950 dark:text-indigo-300", "Pulang Lebih Awal": "bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-300" };

export default function Attendance() {
  const { user } = useAuth();
  const [period, setPeriod, range, setRange] = usePersistentPeriod("attendance-me", "month");
  const [data, setData] = useState(null);
  const [mode, setMode] = useState(null); // "in" | "out"
  const [blob, setBlob] = useState(null);
  const [busy, setBusy] = useState(false);
  const [reqs, setReqs] = useState([]);
  const [reqOpen, setReqOpen] = useState(false);
  const [reqForm, setReqForm] = useState({ type: "izin", date: "", reason: "", leave_type: "sakit", proposed_check_in: "", proposed_check_out: "" });
  const [coords, setCoords] = useState(null);
  const [gpsMsg, setGpsMsg] = useState("");
  const [note, setNote] = useState("");

  const grabLocation = () => {
    setCoords(null);
    setGpsMsg("Membaca lokasi GPS...");
    if (!navigator.geolocation) { setGpsMsg("GPS tidak tersedia di perangkat ini"); return; }
    navigator.geolocation.getCurrentPosition(
      (p) => { setCoords({ lat: p.coords.latitude, lng: p.coords.longitude }); setGpsMsg("Lokasi terbaca"); },
      () => { setCoords(null); setGpsMsg("Lokasi tidak terbaca — aktifkan izin lokasi perangkat"); },
      { enableHighAccuracy: true, timeout: 10000 });
  };

  const qstr = () => period === "custom" ? `period=custom&start=${range.start}&end=${range.end}` : `period=${period}`;
  const load = () => api.get(`/attendance/me?${qstr()}`).then((r) => setData(r.data)).catch((e) => toast.error(formatApiError(e.response?.data?.detail)));
  const loadReqs = () => api.get("/attendance/requests/me").then((r) => setReqs(r.data.rows)).catch(() => {});
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [period, range.start, range.end]);
  useEffect(() => { loadReqs(); }, []);

  const submitReq = async () => {
    if (!reqForm.date || !reqForm.reason) { toast.error("Tanggal & alasan wajib diisi"); return; }
    if (reqForm.type === "koreksi" && !reqForm.proposed_check_in && !reqForm.proposed_check_out) { toast.error("Isi usulan jam masuk/pulang"); return; }
    try {
      await api.post("/attendance/requests", reqForm);
      toast.success("Pengajuan terkirim, menunggu persetujuan Owner");
      setReqOpen(false);
      setReqForm({ type: "izin", date: "", reason: "", leave_type: "sakit", proposed_check_in: "", proposed_check_out: "" });
      loadReqs();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const today = (data?.rows || []).find((r) => r.date === new Date(new Date().getTime() + 7 * 3600 * 1000).toISOString().slice(0, 10));
  const hasIn = !!today?.check_in;
  const hasOut = !!today?.check_out;

  const submit = async () => {
    if (!blob) { toast.error("Foto wajib diambil dulu"); return; }
    if (!note.trim()) { toast.error("Keterangan pekerjaan wajib diisi"); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", blob, "absen.jpg");
      const up = await api.post("/upload/attendance-photo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const endpoint = mode === "in" ? "/attendance/check-in" : "/attendance/check-out";
      await api.post(endpoint, { photo: up.data.path, note: note.trim(), ...(coords || {}) });
      toast.success(mode === "in" ? "Absen masuk tercatat" : "Absen pulang tercatat");
      setMode(null); setBlob(null); setNote(""); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  if (!data) return <PageSkeleton stats={4} rows={5} />;
  const rk = data.rekap || {};

  return (
    <div className="space-y-4" data-testid="attendance-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2.5">
        <div>
          <h2 className="font-heading text-lg sm:text-xl font-bold tracking-tight">Absensi</h2>
          <p className="text-xs sm:text-sm text-muted-foreground">Absen masuk & pulang dengan foto. Waktu dicatat otomatis (WIB).</p>
        </div>
        <PeriodFilter value={period} onChange={setPeriod} range={range} onRangeChange={setRange} />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Card className="p-4 flex items-center justify-between gap-3">
          <div>
            <p className="text-xs uppercase tracking-wider text-muted-foreground">Absen Masuk Hari Ini</p>
            <p className="font-heading text-lg font-bold" data-testid="att-in-time">{hasIn ? formatTime(today.check_in.time) : "Belum absen"}</p>
            {hasIn && <Badge className={`mt-1 text-[10px] ${CI_BADGE[today.check_in.status] || ""}`}>{today.check_in.status}{today.check_in.late_minutes ? ` · ${fmtMin(today.check_in.late_minutes)}` : ""}</Badge>}
          </div>
          <div className="flex flex-col items-end gap-1">
            <Button variant="gold" className="gap-1.5" disabled={hasIn} onClick={() => { setBlob(null); setMode("in"); grabLocation(); }} data-testid="att-checkin-btn">
              <Camera className="h-4 w-4" /> Masuk + Foto
            </Button>
            {!hasIn && <span className="text-[9px] text-rose-500 font-medium">Wajib foto kamera</span>}
          </div>
        </Card>
        <Card className="p-4 flex items-center justify-between gap-3">
          <div>
            <p className="text-xs uppercase tracking-wider text-muted-foreground">Absen Pulang Hari Ini</p>
            <p className="font-heading text-lg font-bold" data-testid="att-out-time">{hasOut ? formatTime(today.check_out.time) : "Belum absen"}</p>
            {hasOut && <Badge className={`mt-1 text-[10px] ${CO_BADGE[today.check_out.status] || ""}`}>{today.check_out.status}</Badge>}
          </div>
          <div className="flex flex-col items-end gap-1">
            <Button variant="gold" className="gap-1.5" disabled={!hasIn || hasOut} onClick={() => { setBlob(null); setMode("out"); grabLocation(); }} data-testid="att-checkout-btn">
              <Camera className="h-4 w-4" /> Pulang + Foto
            </Button>
            {hasIn && !hasOut && <span className="text-[9px] text-rose-500 font-medium">Wajib foto kamera</span>}
          </div>
        </Card>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="att-rekap">
        {[
          { l: "Hari Kerja", v: rk.hari_kerja || 0, i: CalendarCheck, t: ["bg-sky-100 dark:bg-sky-950", "text-sky-600"] },
          { l: "Terlambat", v: `${rk.terlambat || 0}×`, s: fmtMin(rk.total_late_minutes), i: Clock, t: ["bg-rose-100 dark:bg-rose-950", "text-rose-600"] },
          { l: "Lembur", v: fmtMin(rk.total_overtime_minutes), i: Clock, t: ["bg-indigo-100 dark:bg-indigo-950", "text-indigo-600"] },
          { l: "Sisa Kekurangan", v: fmtMin(rk.sisa_kekurangan_minutes), i: Clock, t: ["bg-amber-100 dark:bg-amber-950", "text-amber-600"] },
        ].map((c) => (
          <Card key={c.l} className="p-4" data-testid={`att-stat-${c.l}`}>
            <div className="flex items-center gap-3">
              <div className={`grid place-items-center h-10 w-10 rounded-xl ${c.t[0]}`}><c.i className={`h-5 w-5 ${c.t[1]}`} /></div>
              <div className="min-w-0">
                <p className="text-[11px] uppercase tracking-wider text-muted-foreground">{c.l}</p>
                <p className="font-heading text-base font-bold truncate">{c.v}</p>
                {c.s && <p className="text-[10px] text-muted-foreground">{c.s}</p>}
              </div>
            </div>
          </Card>
        ))}
      </div>

      <Card className="p-4">
        <h3 className="font-heading text-sm font-semibold mb-2">Riwayat Absensi</h3>
        <div className="space-y-1.5" data-testid="att-history">
          {data.rows.length === 0 && <p className="text-sm text-muted-foreground py-4">Belum ada absensi pada periode ini.</p>}
          {data.rows.map((r) => (
            <div key={r._id} className="flex items-center justify-between gap-2 rounded-lg border border-border px-3 py-2 text-sm" data-testid={`att-row-${r._id}`}>
              <div className="min-w-0">
                <p className="font-medium">{formatDate(r.date)}</p>
                <p className="text-[11px] text-muted-foreground">
                  Masuk {r.check_in ? formatTime(r.check_in.time) : "-"} · Pulang {r.check_out ? formatTime(r.check_out.time) : "-"}
                </p>
                {(r.check_in?.note || r.check_out?.note) && (
                  <p className="text-[11px] text-muted-foreground break-words" data-testid={`att-note-row-${r._id}`}>Kerja: {[r.check_in?.note, r.check_out?.note].filter(Boolean).join(" · ")}</p>
                )}
              </div>
              <div className="flex flex-wrap gap-1 justify-end">
                {r.leave && <Badge className="text-[10px] bg-violet-100 text-violet-800 dark:bg-violet-950 dark:text-violet-300">Izin: {r.leave.type}</Badge>}
                {r.check_in && <Badge className={`text-[10px] ${CI_BADGE[r.check_in.status] || ""}`}>{r.check_in.status}</Badge>}
                {r.check_out && <Badge className={`text-[10px] ${CO_BADGE[r.check_out.status] || ""}`}>{r.check_out.status}</Badge>}
              </div>
            </div>
          ))}
        </div>
      </Card>

      <Card className="p-4">
        <div className="flex items-center justify-between mb-2 gap-2">
          <h3 className="font-heading text-sm font-semibold flex items-center gap-1.5"><FileText className="h-4 w-4 text-[#C9A227]" /> Pengajuan Saya (Izin / Koreksi)</h3>
          <Button size="sm" variant="outline" className="gap-1" onClick={() => setReqOpen(true)} data-testid="att-req-new-btn"><Plus className="h-3.5 w-3.5" /> Ajukan</Button>
        </div>
        <div className="space-y-1.5" data-testid="att-req-list">
          {reqs.length === 0 && <p className="text-sm text-muted-foreground py-3">Belum ada pengajuan.</p>}
          {reqs.map((r) => (
            <div key={r._id} className="flex items-center justify-between gap-2 rounded-lg border border-border px-3 py-2 text-sm" data-testid={`att-req-${r._id}`}>
              <div className="min-w-0">
                <p className="font-medium capitalize">{r.type} · {formatDate(r.date)}{r.leave_type ? ` · ${r.leave_type}` : ""}</p>
                <p className="text-[11px] text-muted-foreground truncate">{r.reason}{r.decision_note ? ` — Catatan: ${r.decision_note}` : ""}</p>
              </div>
              <Badge className={`text-[10px] shrink-0 ${r.status === "Disetujui" ? "bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300" : r.status === "Ditolak" ? "bg-rose-100 text-rose-800 dark:bg-rose-950 dark:text-rose-300" : "bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-300"}`}>{r.status}</Badge>
            </div>
          ))}
        </div>
      </Card>

      <Dialog open={!!mode} onOpenChange={(o) => { if (!o) { setMode(null); setBlob(null); } }}>
        <DialogContent className="sm:max-w-sm" data-testid="att-camera-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">{mode === "in" ? "Absen Masuk" : "Absen Pulang"}</DialogTitle>
          </DialogHeader>
          <p className="text-xs text-muted-foreground -mt-1">Ambil foto langsung menggunakan kamera. Foto wajib untuk menyimpan absensi.</p>
          {mode && <CameraCapture dataTestId="att-cam" onCapture={setBlob} watermark={[user?.name || "", `${new Date().toLocaleString("id-ID", { timeZone: "Asia/Jakarta" })} WIB`]} />}
          {gpsMsg && <p className="text-[11px] text-muted-foreground" data-testid="att-gps-status">Lokasi: {gpsMsg}</p>}
          <div className="space-y-1.5">
            <Label className="text-xs">Apa yang Anda kerjakan hari ini? <span className="text-rose-600">(wajib)</span></Label>
            <Textarea value={note} onChange={(e) => setNote(e.target.value)} placeholder="Tulis pekerjaan Anda hari ini..." rows={2} data-testid="att-note" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setMode(null); setBlob(null); }}>Batal</Button>
            <div className="flex flex-col items-end gap-0.5">
              {!blob && <span className="text-[10px] text-rose-500 font-medium" data-testid="att-photo-required-hint">Ambil foto dulu untuk menyimpan</span>}
              {blob && !note.trim() && <span className="text-[10px] text-rose-500 font-medium" data-testid="att-note-required-hint">Isi keterangan pekerjaan dulu</span>}
              <Button variant="gold" disabled={!blob || !note.trim() || busy} onClick={submit} data-testid="att-save-btn">{busy ? "Menyimpan..." : "Simpan Absensi"}</Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={reqOpen} onOpenChange={setReqOpen}>
        <DialogContent className="sm:max-w-sm" data-testid="att-req-dialog">
          <DialogHeader><DialogTitle className="font-heading">Ajukan Izin / Koreksi</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label className="text-xs">Jenis Pengajuan</Label>
              <Select value={reqForm.type} onValueChange={(v) => setReqForm({ ...reqForm, type: v })}>
                <SelectTrigger data-testid="att-req-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="izin">Izin (sakit/cuti/izin)</SelectItem>
                  <SelectItem value="koreksi">Koreksi Jam Absensi</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5"><Label className="text-xs">Tanggal</Label><Input type="date" value={reqForm.date} onChange={(e) => setReqForm({ ...reqForm, date: e.target.value })} data-testid="att-req-date" /></div>
            {reqForm.type === "izin" && (
              <div className="space-y-1.5">
                <Label className="text-xs">Jenis Izin</Label>
                <Select value={reqForm.leave_type} onValueChange={(v) => setReqForm({ ...reqForm, leave_type: v })}>
                  <SelectTrigger data-testid="att-req-leavetype"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="sakit">Sakit</SelectItem>
                    <SelectItem value="cuti">Cuti</SelectItem>
                    <SelectItem value="izin">Izin</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
            {reqForm.type === "koreksi" && (
              <div className="grid grid-cols-2 gap-2">
                <div className="space-y-1.5"><Label className="text-xs">Usulan Masuk</Label><Input type="time" value={reqForm.proposed_check_in} onChange={(e) => setReqForm({ ...reqForm, proposed_check_in: e.target.value })} data-testid="att-req-ci" /></div>
                <div className="space-y-1.5"><Label className="text-xs">Usulan Pulang</Label><Input type="time" value={reqForm.proposed_check_out} onChange={(e) => setReqForm({ ...reqForm, proposed_check_out: e.target.value })} data-testid="att-req-co" /></div>
              </div>
            )}
            <div className="space-y-1.5"><Label className="text-xs">Alasan</Label><Textarea value={reqForm.reason} onChange={(e) => setReqForm({ ...reqForm, reason: e.target.value })} data-testid="att-req-reason" placeholder="Jelaskan alasan pengajuan" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReqOpen(false)}>Batal</Button>
            <Button variant="gold" onClick={submitReq} data-testid="att-req-submit">Kirim Pengajuan</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
